<template>
  <div class="modal is-active">
    <div class="modal-background" @click.stop></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ step === 1 ? "Personalization" : "Details" }}
        </p>
      </header>
      <section class="modal-card-body">
        <div class="signup-stepper">
          <p class="has-text-centered has-text-weight-semibold">
            Step {{ step }} of 2
          </p>
        </div>

        <div
          class="notification is-danger is-light"
          v-if="errorMessage"
          v-bind:class="{
            'm-fadeIn': errorMessage,
            'm-fadeOut': !errorMessage
          }"
        >
          {{ errorMessage }}
        </div>

        <!-- Step 1: User Type Selection -->
        <div v-show="step == 1" class="step-content">
          <h4 class="title is-5">Let's personalize your experience</h4>
          <p class="subtitle is-6">
            Choose the option that best fits your primary role so we can
            customize Gobo for your needs.
          </p>

          <div class="user-type-cards">
            <div
              class="card user-type-card"
              :class="{ 'is-selected': userType === 'venue' }"
              @click="userType = 'venue'"
            >
              <div class="card-content">
                <div class="media">
                  <div class="media-left">
                    <span class="icon is-large">
                      <i class="fas fa-building fa-2x"></i>
                    </span>
                  </div>
                  <div class="media-content">
                    <p class="title is-6">Venue Manager</p>
                    <p class="subtitle is-7">
                      I manage a physical location that hosts performances
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="card user-type-card"
              :class="{ 'is-selected': userType === 'artist' }"
              @click="userType = 'artist'"
            >
              <div class="card-content">
                <div class="media">
                  <div class="media-left">
                    <span class="icon is-large">
                      <i class="fas fa-music fa-2x"></i>
                    </span>
                  </div>
                  <div class="media-content">
                    <p class="title is-6">Artist/Performer</p>
                    <p class="subtitle is-7">
                      I'm a musician, comedian, theater performer, or
                      entertainer
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="card user-type-card"
              :class="{ 'is-selected': userType === 'festival' }"
              @click="userType = 'festival'"
            >
              <div class="card-content">
                <div class="media">
                  <div class="media-left">
                    <span class="icon is-large">
                      <i class="fas fa-calendar-alt fa-2x"></i>
                    </span>
                  </div>
                  <div class="media-content">
                    <p class="title is-6">Festival Organizer</p>
                    <p class="subtitle is-7">
                      I organize festivals or recurring events with multiple
                      artists
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="card user-type-card"
              :class="{ 'is-selected': userType === 'other' }"
              @click="userType = 'other'"
            >
              <div class="card-content">
                <div class="media">
                  <div class="media-left">
                    <span class="icon is-large">
                      <i class="fas fa-users fa-2x"></i>
                    </span>
                  </div>
                  <div class="media-content">
                    <p class="title is-6">Other Event Organizer</p>
                    <p class="subtitle is-7">
                      I organize events that don't fit the above
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Type-specific Details -->
        <div v-show="step == 2" class="step-content">
          <div v-if="userType === 'venue'" class="venue-details">
            <h4 class="title is-5">Tell us about your venue</h4>

            <div class="field">
              <label class="label">Venue Location</label>
              <div class="control">
                <VenueFinder @venueFound="setVenueLocation" />
              </div>
              <p class="help">
                Start typing your venue's address to find it using Google Maps
              </p>
            </div>

            <div v-if="venueData.location" class="field">
              <label class="label">Venue Capacity</label>
              <div class="control">
                <input
                  class="input"
                  type="number"
                  placeholder="Maximum number of people your venue can hold"
                  v-model.number="venueData.capacity"
                />
              </div>
            </div>

            <div v-if="venueData.location" class="field">
              <label class="label">Venue Category</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="venueData.category">
                    <option value="">Select a category</option>
                    <option value="music">Music Venue</option>
                    <option value="performing-arts">
                      Performing Arts Theater
                    </option>
                    <option value="comedy">Comedy Club</option>
                    <option value="multi-use">Multi-Use Space</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div v-if="userType === 'artist'" class="artist-details">
            <h4 class="title is-5">Tell us about your act</h4>

            <div class="field">
              <label class="label">Artist or Group Name</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  placeholder="Your stage name or band name"
                  v-model="artistData.name"
                />
              </div>
            </div>

            <div class="field">
              <label class="label">Performance Category</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="artistData.category">
                    <option value="">Select a category</option>
                    <option value="music">Music</option>
                    <option value="performing-arts">Performing Arts</option>
                    <option value="comedy">Comedy</option>
                  </select>
                </div>
              </div>
            </div>

            <div v-if="artistData.category === 'music'" class="field">
              <label class="label">Music Genre</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="artistData.genre">
                    <option value="">Select a genre</option>
                    <option value="rock">Rock</option>
                    <option value="pop">Pop</option>
                    <option value="jazz">Jazz</option>
                    <option value="electronic">Electronic</option>
                    <option value="hip-hop">Hip-Hop</option>
                    <option value="country">Country</option>
                    <option value="folk">Folk</option>
                    <option value="classical">Classical</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            <div v-if="artistData.category === 'performing-arts'" class="field">
              <label class="label">Performing Arts Genre</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="artistData.genre">
                    <option value="">Select a genre</option>
                    <option value="theater">Theater</option>
                    <option value="dance">Dance</option>
                    <option value="opera">Opera</option>
                    <option value="ballet">Ballet</option>
                    <option value="musical-theater">Musical Theater</option>
                    <option value="spoken-word">Spoken Word</option>
                    <option value="improv">Improv</option>
                    <option value="magic">Magic</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div v-if="userType === 'festival'" class="festival-details">
            <h4 class="title is-5">Tell us about your festival</h4>

            <div class="field">
              <label class="label">Festival Name</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  placeholder="Name of your festival or event series"
                  v-model="festivalData.name"
                />
              </div>
            </div>

            <div class="field">
              <label class="label">Festival Location</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  placeholder="City, State/Province (e.g., Minneapolis, MN)"
                  v-model="festivalData.location"
                />
              </div>
              <p class="help">Where does your festival typically take place?</p>
            </div>

            <div v-if="festivalData.location" class="field">
              <label class="label">Frequency</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="festivalData.frequency">
                    <option value=""
                      >How often does this festival happen?</option
                    >
                    <option value="annual">Annual</option>
                    <option value="semi-annual">
                      Semi-Annual (twice a year)
                    </option>
                    <option value="quarterly">Quarterly</option>
                    <option value="monthly">Monthly</option>
                    <option value="one-time">One-time event</option>
                    <option value="irregular">Irregular schedule</option>
                  </select>
                </div>
              </div>
            </div>

            <div v-if="festivalData.location" class="field">
              <label class="label">Primary Focus</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="festivalData.primaryFocus">
                    <option value="">
                      Select the primary focus of your festival
                    </option>
                    <option value="music">Music</option>
                    <option value="comedy">Comedy</option>
                    <option value="theater">Theater</option>
                    <option value="film">Film</option>
                    <option value="multi-genre">Multi-Genre</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div v-if="userType === 'other'" class="other-details">
            <h4 class="title is-5">Tell us about the events you organize</h4>

            <div class="field">
              <label class="label">Event Description</label>
              <div class="control">
                <textarea
                  class="textarea"
                  placeholder="Describe the types of events you create and organize"
                  v-model="otherData.description"
                  rows="4"
                ></textarea>
              </div>
              <p class="help">
                Help us understand what kinds of events you organize so we can
                better tailor Gobo to your needs.
              </p>
            </div>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot">
        <button class="button" @click="previousStep" v-if="step > 1">
          Back
        </button>
        <button
          class="button is-primary"
          @click="nextStep"
          :disabled="!canProceed"
        >
          {{ step === 1 ? "Next" : "Get Started" }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import firebase from "firebase/app";
import "firebase/firestore";
import VenueFinder from "@/components/venue/venue-finder";

export default {
  name: "OnboardingModal",
  components: {
    VenueFinder
  },
  props: {
    userName: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      step: 1,
      errorMessage: "",
      userType: "",
      venueData: {
        location: null,
        capacity: null,
        category: ""
      },
      artistData: {
        name: "",
        category: "",
        genre: ""
      },
      festivalData: {
        name: "",
        location: "",
        frequency: "",
        primaryFocus: ""
      },
      otherData: {
        description: ""
      }
    };
  },
  computed: {
    canProceed() {
      if (this.step === 1) {
        return this.userType !== "";
      }
      if (this.step === 2) {
        switch (this.userType) {
          case "venue":
            return (
              this.venueData.location &&
              this.venueData.capacity &&
              this.venueData.category
            );
          case "artist":
            return (
              this.artistData.name &&
              this.artistData.category &&
              (!["music", "performing-arts"].includes(
                this.artistData.category
              ) ||
                this.artistData.genre)
            );
          case "festival":
            return (
              this.festivalData.name &&
              this.festivalData.location.trim() &&
              this.festivalData.frequency &&
              this.festivalData.primaryFocus
            );
          case "other":
            return this.otherData.description.trim().length > 0;
          default:
            return false;
        }
      }
      return false;
    }
  },
  methods: {
    async nextStep() {
      if (this.step === 1) {
        this.step = 2;
      } else if (this.step === 2) {
        await this.completeOnboarding();
      }
    },
    previousStep() {
      if (this.step > 1) {
        this.step--;
      }
    },
    setVenueLocation(venue) {
      this.venueData.location = venue;
    },
    async completeOnboarding() {
      try {
        const db = firebase.firestore();
        const userRef = db
          .collection("users")
          .doc(firebase.auth().currentUser.uid);

        // Prepare the update data
        const updateData = {
          userType: this.userType,
          onboardingComplete: true,
          onboardingCompletedAt: firebase.firestore.FieldValue.serverTimestamp()
        };

        // Add type-specific flags for compatibility with existing system
        switch (this.userType) {
          case "venue":
            updateData.venueManager = true;
            break;
          case "artist":
            updateData.performingArtist = true;
            updateData.artistType = {
              name: this.artistData.name,
              category: this.artistData.category,
              genre: this.artistData.genre || null
            };
            break;
          case "festival":
            updateData.festivalManager = true;
            updateData.festivalType = {
              name: this.festivalData.name,
              location: this.festivalData.location,
              frequency: this.festivalData.frequency,
              primaryFocus: this.festivalData.primaryFocus
            };
            break;
          case "other":
            updateData.otherEventOrganizer = true;
            updateData.otherType = {
              description: this.otherData.description
            };
            break;
        }

        // Update user record first
        await userRef.update(updateData);

        // Fetch the updated user data to ensure Vuex store is in sync
        await this.$store.dispatch("fetchUser", firebase.auth().currentUser);

        // Now create profiles after user data is properly loaded in Vuex store
        switch (this.userType) {
          case "venue":
            await this.saveVenueUsingVuex();
            break;
          case "artist":
            await this.createArtistProfile();
            break;
          case "festival":
            await this.createFestivalProfile();
            break;
        }

        // For artists and venue managers, explicitly fetch their profiles
        if (this.userType === "artist") {
          await this.$store.dispatch(
            "fetchProfiles",
            firebase.auth().currentUser.uid
          );
        }
        if (this.userType === "venue") {
          await this.$store.dispatch(
            "fetchMyVenues",
            firebase.auth().currentUser.uid
          );
        }
        if (this.userType === "festival") {
          await this.$store.dispatch(
            "fetchMyFestivals",
            firebase.auth().currentUser.uid
          );
        }

        console.log("Onboarding completed successfully");
        this.$emit("completed");
      } catch (error) {
        console.error("Error saving onboarding data:", error);
        this.errorMessage =
          "There was an error saving your information. Please try again.";
      }
    },
    async saveVenueUsingVuex() {
      try {
        const venue = this.venueData.location;
        const user = firebase.auth().currentUser;

        venue.owner = user.uid;
        venue.owners = {};
        venue.owners[user.uid] = { name: this.userName };
        venue.capacity = this.venueData.capacity;
        venue.category = this.venueData.category;

        await this.$store.dispatch("addVenueProfile", venue);
        console.log("Venue saved using Vuex action with ID:", venue.place_id);
      } catch (error) {
        console.error("Error saving venue using Vuex:", error);
        throw error;
      }
    },
    async createArtistProfile() {
      try {
        const user = firebase.auth().currentUser;

        await this.$store.dispatch("addNewProfile", user);

        const artistProfile = {
          name: this.artistData.name,
          type: this.artistData.category,
          genre: this.artistData.genre || null,
          links: {
            web: "",
            instagram: "",
            facebook: "",
            twitter: ""
          }
        };

        await this.$store.dispatch("createPerformerProfile", artistProfile);
        console.log("Artist profile created using Vuex action");
      } catch (error) {
        console.error("Error creating artist profile:", error);
        throw error;
      }
    },
    async createFestivalProfile() {
      try {
        const user = firebase.auth().currentUser;

        const festivalProfile = {
          name: this.festivalData.name,
          location: this.festivalData.location,
          frequency: this.festivalData.frequency,
          primaryFocus: this.festivalData.primaryFocus,
          owner: user.uid,
          owners: {
            [user.uid]: {
              name: this.userName
            }
          },
          tax: "remit"
        };

        await this.$store.dispatch("createFestivalProfile", festivalProfile);
        console.log("Festival profile created using Vuex action");
      } catch (error) {
        console.error("Error creating festival profile:", error);
        throw error;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.modal-card {
  max-width: 600px;
  width: 90%;
}

.modal-card-head {
  background-color: $active-green;
}

.modal-card-title {
  color: white;
}

.modal-card-foot {
  justify-content: flex-end;
}

.m-fadeOut {
  visibility: hidden;
  opacity: 0;
  height: 0;
  padding-top: 0;
  padding-bottom: 0;
  transition: visibility 0s linear 600ms, opacity 600ms, height 600ms,
    padding-top 600ms;
}

.m-fadeIn {
  visibility: visible;
  opacity: 1;
  transition: visibility 0s linear 0s, opacity 600ms, height 600ms,
    padding-top 600ms, padding-bottom 600ms;
}

.signup-stepper {
  margin-bottom: 2rem;

  p {
    color: $active-green;
    font-size: 1rem;
  }
}

.step-content {
  margin-bottom: 1.5rem;
}

.user-type-cards {
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
}

.user-type-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  &.is-selected {
    border-color: $active-green;
    box-shadow: 0 4px 12px rgba($active-green, 0.3);
  }
}

.field {
  margin-bottom: 1.5rem;
}

.help {
  font-size: 0.875rem;
  color: $gray4;
}

.card-content {
  padding: 1rem;
}
</style>
